import api from '../../API/baseURL';
import { handleAuthErrors } from '../../utils/vsCodeUtils';

// API endpoints for Team Dashboard
const TEAM_ENDPOINTS = {
  // Token Usage
  TEAM_TOKEN_USAGE: '/teams', // POST /teams/{team_id}/token-usage
  TEAM_USAGE_OVERVIEW: '/teams', // GET /teams/{team_id}/usage
  TEAM_MEMBERS_USAGE: '/teams', // GET /teams/{team_id}/usage/members
  TEAM_USAGE_HISTORY: '/teams', // GET /teams/{team_id}/usage/history

  // Team Management
  MY_TEAM: '/teams/my-team',
  TEAM_BY_ID: '/teams', // GET /teams/{team_id}
  UPDATE_TEAM: '/teams', // PATCH /teams/{team_id}
  REGENERATE_INVITE: '/teams', // POST /teams/{team_id}/regenerate-invite
  REMOVE_MEMBER: '/teams', // DELETE /teams/{team_id}/members/{user_id}
  SEND_INVITES: '/teams/send_invites',
  LEAVE_TEAM: '/teams/leave',
  UPDATE_SEATS: '/teams/update_seats',
  ACTIVITY_LOG: '/teams', // GET /teams/{team_id}/activity
};

// Helper to get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('web_access_token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// =====================
// TOKEN USAGE ENDPOINTS
// =====================

// Get team token usage (same format as old /get_user_token_usage)
// POST /teams/{team_id}/token-usage
export const getTeamTokenUsage = async (teamId, params) => {
  try {
    const response = await api.post(
      `${TEAM_ENDPOINTS.TEAM_TOKEN_USAGE}/${teamId}/token-usage`,
      {
        data_type: params.data_type,
        member_id: params.member_id, // Optional - omit for team aggregate
        timezone_offset_minutes: params.timezone_offset_minutes || new Date().getTimezoneOffset(),
        year: params.year || new Date().getFullYear(),
        month: params.month,
        day: params.day,
        aggregate: params.aggregate || false
      },
      { headers: getAuthHeader() }
    );
    console.log('Team Token Usage:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching team token usage:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Get team usage overview
// GET /teams/{team_id}/usage?period=month
export const getTeamUsageOverview = async (teamId, period = 'month') => {
  try {
    const response = await api.get(
      `${TEAM_ENDPOINTS.TEAM_USAGE_OVERVIEW}/${teamId}/usage?period=${period}`,
      { headers: getAuthHeader() }
    );
    console.log('Team Usage Overview:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching team usage overview:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Get all team members' usage
// GET /teams/{team_id}/usage/members?period=month
export const getTeamMembersUsage = async (teamId, period = 'month') => {
  try {
    const response = await api.get(
      `${TEAM_ENDPOINTS.TEAM_MEMBERS_USAGE}/${teamId}/usage/members?period=${period}`,
      { headers: getAuthHeader() }
    );
    console.log('Team Members Usage:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching team members usage:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Get single member usage
// GET /teams/{team_id}/usage/members/{user_id}?period=month
export const getMemberUsage = async (teamId, userId, period = 'month') => {
  try {
    const response = await api.get(
      `${TEAM_ENDPOINTS.TEAM_MEMBERS_USAGE}/${teamId}/usage/members/${userId}?period=${period}`,
      { headers: getAuthHeader() }
    );
    console.log('Member Usage:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching member usage:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Get team usage history for charts
// GET /teams/{team_id}/usage/history?granularity=day&range=30d
export const getTeamUsageHistory = async (teamId, granularity = 'day', range = '30d') => {
  try {
    const response = await api.get(
      `${TEAM_ENDPOINTS.TEAM_USAGE_HISTORY}/${teamId}/usage/history?granularity=${granularity}&range=${range}`,
      { headers: getAuthHeader() }
    );
    console.log('Team Usage History:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching team usage history:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// =========================
// TEAM MANAGEMENT ENDPOINTS
// =========================

// Get current user's team
// GET /teams/my-team
export const getMyTeam = async () => {
  try {
    const response = await api.get(TEAM_ENDPOINTS.MY_TEAM, {
      headers: getAuthHeader()
    });
    console.log('My Team:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching my team:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Get team by ID
// GET /teams/{team_id}
export const getTeamById = async (teamId) => {
  try {
    const response = await api.get(
      `${TEAM_ENDPOINTS.TEAM_BY_ID}/${teamId}`,
      { headers: getAuthHeader() }
    );
    console.log('Team by ID:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching team by ID:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Update team details
// PATCH /teams/{team_id}
export const updateTeam = async (teamId, data) => {
  try {
    const response = await api.patch(
      `${TEAM_ENDPOINTS.UPDATE_TEAM}/${teamId}`,
      { team_name: data.team_name },
      { headers: getAuthHeader() }
    );
    console.log('Team Updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating team:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Regenerate invite code
// POST /teams/{team_id}/regenerate-invite
export const regenerateInviteCode = async (teamId) => {
  try {
    const response = await api.post(
      `${TEAM_ENDPOINTS.REGENERATE_INVITE}/${teamId}/regenerate-invite`,
      {},
      { headers: getAuthHeader() }
    );
    console.log('Invite Code Regenerated:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error regenerating invite code:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Remove member from team
// DELETE /teams/{team_id}/members/{user_id}
export const removeMember = async (teamId, userId) => {
  try {
    const response = await api.delete(
      `${TEAM_ENDPOINTS.REMOVE_MEMBER}/${teamId}/members/${userId}`,
      { headers: getAuthHeader() }
    );
    console.log('Member Removed:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error removing member:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Send team invites
// POST /teams/send_invites
export const sendInvites = async (teamId, emails, inviteLink = null) => {
  try {
    const body = {
      team_id: teamId,
      emails: emails
    };
    if (inviteLink) {
      body.invite_link = inviteLink;
    }

    const response = await api.post(
      TEAM_ENDPOINTS.SEND_INVITES,
      body,
      { headers: getAuthHeader() }
    );
    console.log('Invites Sent:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error sending invites:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Leave team (members only)
// POST /teams/leave
export const leaveTeam = async () => {
  try {
    const response = await api.post(
      TEAM_ENDPOINTS.LEAVE_TEAM,
      {},
      { headers: getAuthHeader() }
    );
    console.log('Left Team:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error leaving team:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Update seats (add more seats)
// POST /teams/update_seats
export const updateSeats = async (additionalSeats) => {
  try {
    const response = await api.post(
      TEAM_ENDPOINTS.UPDATE_SEATS,
      { additional_seats: additionalSeats },
      { headers: getAuthHeader() }
    );
    console.log('Seats Updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating seats:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Get team activity log
// GET /teams/{team_id}/activity?limit=50&offset=0
export const getActivityLog = async (teamId, limit = 50, offset = 0) => {
  try {
    const response = await api.get(
      `${TEAM_ENDPOINTS.ACTIVITY_LOG}/${teamId}/activity?limit=${limit}&offset=${offset}`,
      { headers: getAuthHeader() }
    );
    console.log('Activity Log:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching activity log:', error);
    handleAuthErrors(error);
    throw error;
  }
};

export default TEAM_ENDPOINTS;
